import { useEffect, useMemo, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import clsx from 'clsx';
import __isEmpty from 'lodash/isEmpty';
import Text from '../../../components/corporatiu/Text';
// @ts-ignore
import Enllac from '../../../components/corporatiu/Enllac';
// @ts-ignore
import Imatge from '../../../components/corporatiu/Imatge';
import styles from './tabs.module.scss';

const EnllacLink = Enllac as ComponentType<{
    href: string;
    title?: string;
    className?: string;
    children?: ReactNode;
}>;

interface TabInput {
    label?: string;
    titol?: string;
    nom?: string;
    content?: string;
    html?: string;
    descripcio?: string;
    text?: string;
    contingut?: string;
    href?: string;
    url?: string;
    link?: string;
    iframe?: string;
    formulari?: string;
    logo?: string;
    logoWidth?: string | number;
    logoHeight?: string | number;
}

interface TabsProps {
    tabs?: TabInput[];
    id?: string;
    className?: string;
    estil?: string;
}

interface NormalizedTab {
    label: string;
    content: string;
    href: string;
    iframe: string;
    logo: string;
    logoWidth: string | number;
    logoHeight: string | number;
}

const DEFAULT_TABS: NormalizedTab[] = [
    {
        label: '3Cat',
        logo: '/_resources/logos/3cat_color.svg',
        logoWidth: 80,
        logoHeight: 23,
        content: '',
        href: '',
        iframe: ''
    },
    {
        label: 'TV3',
        logo: '/_resources/logos/tv3_colorBgBlanc.svg',
        logoWidth: 40,
        logoHeight: 40,
        content: '',
        href: '',
        iframe: ''
    },
    {
        label: 'Catalunya Ràdio',
        logo: '/_resources/logos/catradio_colorBgBlanc.svg',
        logoWidth: 40,
        logoHeight: 40,
        content: '',
        href: '',
        iframe: ''
    },
    {
        label: 'SX3',
        logo: '/_resources/logos/sx3_colorBgBlanc.svg',
        logoWidth: 55,
        logoHeight: 27,
        content: '',
        href: '',
        iframe: ''
    }
];

const getTabLabel = (tab: TabInput, index: number) => tab?.label || tab?.titol || tab?.nom || DEFAULT_TABS[index]?.label;

const getTabContent = (tab: TabInput) => tab?.content || tab?.html || tab?.descripcio || tab?.text || tab?.contingut || '';

const getTabHref = (tab: TabInput) => tab?.href || tab?.url || tab?.link || '';

const getTabIframe = (tab: TabInput) => tab?.iframe || tab?.formulari || '';

const getTabLogo = (tab: TabInput, index: number) => tab?.logo || DEFAULT_TABS[index]?.logo || '';

const getTabLogoWidth = (tab: TabInput, index: number) => tab?.logoWidth || DEFAULT_TABS[index]?.logoWidth || 40;

const getTabLogoHeight = (tab: TabInput, index: number) => tab?.logoHeight || DEFAULT_TABS[index]?.logoHeight || 40;

const normalizeTabs = (tabs?: TabInput[]) => {
    if (!Array.isArray(tabs) || __isEmpty(tabs)) {
        return DEFAULT_TABS;
    }

    return DEFAULT_TABS.map((defaultTab, index) => {
        const tab = tabs[index] || {};
        const content = getTabContent(tab);
        const href = getTabHref(tab) || defaultTab?.href || '';
        const iframe = getTabIframe(tab);
        const hasHref = !__isEmpty(href);
        const resolvedContent = __isEmpty(content) ? defaultTab.content : content;

        return {
            label: getTabLabel(tab, index) || defaultTab.label,
            logo: getTabLogo(tab, index),
            logoWidth: getTabLogoWidth(tab, index),
            logoHeight: getTabLogoHeight(tab, index),
            content: hasHref ? '' : resolvedContent,
            iframe,
            href
        };
    });
};

export default function Tabs(props: Readonly<TabsProps>) {
    const { tabs, id = 'tabs-contacta', className, estil } = props;
    const normalizedTabs = useMemo(() => normalizeTabs(tabs), [tabs]);
    const [activeTab, setActiveTab] = useState(0);
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);
    const estilKey = estil as keyof typeof styles;

    const stylesTabs = clsx(styles.tabs, estil && styles[estilKey], !__isEmpty(className) && className);

    return (
        <div className={stylesTabs}>
            <div className={styles.tabList} role='tablist' aria-label='Canals de contacte'>
                {normalizedTabs.map((tab, index) => {
                    const isActive = activeTab === index;
                    const hasHref = !__isEmpty(tab.href);
                    const tabId = `${id}-tab-${index}`;
                    const panelId = `${id}-panel-${index}`;

                    if (hasHref) {
                        return (
                            <EnllacLink
                                key={tabId}
                                href={tab.href}
                                className={clsx(styles.tabButton, {
                                    [styles.tabButtonActive]: isActive
                                })}
                                title={tab.label}
                            >
                                <Imatge src={tab.logo} alt={tab.label} width={tab.logoWidth} height={tab.logoHeight} />
                            </EnllacLink>
                        );
                    }

                    return (
                        <button
                            key={tabId}
                            id={tabId}
                            role='tab'
                            type='button'
                            aria-selected={isActive}
                            aria-controls={panelId}
                            tabIndex={isActive ? -1 : 0}
                            className={clsx(styles.tabButton, {
                                [styles.tabButtonActive]: isActive
                            })}
                            onClick={() => setActiveTab(index)}
                        >
                            <Imatge src={tab.logo} alt={tab.label} width={tab.logoWidth} height={tab.logoHeight} />
                        </button>
                    );
                })}
            </div>

            {normalizedTabs.map((tab, index) => {
                if (!__isEmpty(tab.href)) {
                    return null;
                }

                const isActive = activeTab === index;
                const tabId = `${id}-tab-${index}`;
                const panelId = `${id}-panel-${index}`;

                return (
                    <div
                        key={panelId}
                        id={panelId}
                        role='tabpanel'
                        aria-labelledby={tabId}
                        hidden={!isActive}
                        className={clsx(styles.tabPanel, {
                            [styles.tabPanelActive]: isActive
                        })}
                    >
                        {!__isEmpty(tab.content) && <Text text={tab.content} />}
                        {isMounted && isActive && !__isEmpty(tab.iframe) && (
                            <iframe
                                src={tab.iframe}
                                title={`IFrame ${tab.label}`}
                                className={styles.tabFormIframe}
                                width='100%'
                                height='1970'
                            ></iframe>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
